import {
  EDGE_QUALITY,
  type EdgeQuality,
  type Filo,
  type Inclinazione,
  type Lato,
  type Zone,
} from '../config/constants';
import type { StrokeClassification } from '../hit-detection/strokeClassify';

/**
 * A drill target: each axis is either pinned to a value or `undefined` ("qualsiasi" —
 * not evaluated). `minEdge` is the cleanliness bar a matching cut must also clear.
 */
export type DrillTarget = {
  lato?: Lato;
  inclinazione?: Inclinazione;
  filo?: Filo;
  zone?: Zone;
  minEdge: EdgeQuality;
};

/** Which targeted axis the classifier couldn't read (null), or which condition failed. */
export type MismatchReason = 'zona' | 'lato' | 'inclinazione' | 'filo' | 'piattonata';
export type AmbiguousAxis = 'lato' | 'inclinazione' | 'filo';

/** Outcome of one cut against a drill target. */
export type RepVerdict =
  | { kind: 'match' }
  | { kind: 'mismatch'; reason: MismatchReason }
  | { kind: 'ambiguo'; axis: AmbiguousAxis };

/** Rank for edge-quality comparison; `thrust` never satisfies a cut's cleanliness bar. */
const EDGE_RANK: Record<EdgeQuality, number> = {
  [EDGE_QUALITY.good]: 2,
  [EDGE_QUALITY.ok]: 1,
  [EDGE_QUALITY.flat]: 0,
  [EDGE_QUALITY.thrust]: -1,
};

function edgeMeets(q: EdgeQuality | null, min: EdgeQuality): boolean {
  return q !== null && EDGE_RANK[q] >= EDGE_RANK[min];
}

/**
 * Evaluate one cut against a drill target. Pure. Applies the coaching cascade (most
 * fundamental fault first), so the caller can surface a single dominant reason:
 *
 *   1. zona errata (spatial, always known)
 *   2. asse definitivamente sbagliato — lato → inclinazione → filo (classifier read it, wrong)
 *   3. ambiguo su un asse targettato (classifier returned null — "non riconosciuto")
 *   4. piattonata (assi giusti ma edge sotto `minEdge`)
 *
 * An axis left `undefined` in the target is "qualsiasi" and skipped on every step (its
 * value, including ambiguo, is irrelevant).
 */
export function evaluateRep(
  stroke: StrokeClassification,
  zone: Zone | null,
  edgeQuality: EdgeQuality | null,
  target: DrillTarget,
): RepVerdict {
  // 1. Zona
  if (target.zone !== undefined && zone !== target.zone) {
    return { kind: 'mismatch', reason: 'zona' };
  }

  // 2. Asse definitivamente sbagliato (read but wrong), lato → inclinazione → filo.
  if (target.lato !== undefined && stroke.lato !== null && stroke.lato !== target.lato) {
    return { kind: 'mismatch', reason: 'lato' };
  }
  if (
    target.inclinazione !== undefined &&
    stroke.inclinazione !== null &&
    stroke.inclinazione !== target.inclinazione
  ) {
    return { kind: 'mismatch', reason: 'inclinazione' };
  }
  if (target.filo !== undefined && stroke.filo !== null && stroke.filo !== target.filo) {
    return { kind: 'mismatch', reason: 'filo' };
  }

  // 3. Ambiguo su un asse targettato (non riconosciuto), stesso ordine.
  if (target.lato !== undefined && stroke.lato === null) return { kind: 'ambiguo', axis: 'lato' };
  if (target.inclinazione !== undefined && stroke.inclinazione === null) {
    return { kind: 'ambiguo', axis: 'inclinazione' };
  }
  if (target.filo !== undefined && stroke.filo === null) return { kind: 'ambiguo', axis: 'filo' };

  // 4. Assi giusti ma taglio non pulito.
  if (!edgeMeets(edgeQuality, target.minEdge)) {
    return { kind: 'mismatch', reason: 'piattonata' };
  }

  return { kind: 'match' };
}
